<template>
  <div>
    <div class="TipTapWrap" :class="(disabled ? 'disabled' : '') + (label ? 'mt-4' : '')">
      <div class="tiptap-toolbar" v-if="!noButtons">
        <i
          :class="{ 'is-active': editor && editor.isActive('bold') }"
          v-tooltip="'Toggle Bold'"
          @click="addonClick('toggleBold')"
          class="icofont-bold"
        ></i>
        <i
          :class="{ 'is-active': editor && editor.isActive('italic') }"
          v-tooltip="'Toggle Italic'"
          @click="addonClick('toggleItalic')"
          class="icofont-italic"
        ></i>
        <i
          :class="{ 'is-active': editor && editor.isActive('strike') }"
          v-tooltip="'Toggle Strike'"
          @click="addonClick('toggleStrike')"
          class="icofont-strike-through"
        ></i>
        <i
          v-tooltip="'Insert Horizontal Rule'"
          @click="addonClick('setHorizontalRule')"
          class="icofont-minus"
        ></i>
        <i
          v-tooltip="'Undo'"
          @click="addonClick('undo')"
          class="icofont-undo"
        ></i>
        <i
          v-tooltip="'Redo'"
          @click="addonClick('redo')"
          class="icofont-redo"
        ></i>
        <i
          :class="{ 'is-active': editor && editor.isActive('blockquote') }"
          v-tooltip="'Toggle Blockquote'"
          @click="addonClick('toggleBlockquote')"
          class="icofont-quote-right"
        ></i>
        <i
          :class="{ 'is-active': editor && editor.isActive('codeblock') }"
          v-tooltip="'Toggle CodeBlock'"
          @click="addonClick('toggleCodeBlock')"
          class="icofont-code-alt"
        ></i>
        <i
          :class="{ 'is-active': editor && editor.isActive('bulletlist') }"
          v-tooltip="'Toggle BulletList'"
          @click="addonClick('toggleBulletList')"
          class="icofont-listine-dots"
        ></i>
        <i
          :class="{ 'is-active': editor && editor.isActive('orderedlist') }"
          v-tooltip="'Toggle OrderedList'"
          @click="addonClick('toggleOrderedList')"
          class="icofont-listing-number"
        ></i>
        <i
          :class="{ 'is-active': editor && editor.isActive('tasklist') }"
          v-tooltip="'Toggle CheckList'"
          @click="addonClick('toggleTaskList')"
          class="icofont-listing-number"
        ></i>
        <i
          :class="{
            'is-active': editor && editor.isActive('heading', { level: 3 })
          }"
          v-tooltip="'Toggle Heading 3'"
          @click="addonClick('toggleHeading', { level: 3 })"
          class="icofont-heading"
        ></i>
      </div>
      <div
        ref="editorWrap"
        :style="minHeight ? 'min-height: ' + minHeight + 'px' : ''"
      >
        <EditorContent
          class="editorContent"
          :class="hasValue || focused ? 'has-content' : ''"
          @input.stop
          :editor="editor"
          :style="
            [
              maxRows ? 'max-height:' + maxRows * 36 + 'px' : '',
              minRows ? 'min-height:' + maxRows * 36 + 'px' : ''
            ].join(';')
          "
        />
        <label v-if="label" v-html="label"></label>
      </div>
    </div>
    <div class="error-text ml-2 mt-2" v-if="error">
      <i class="icofont-error mr-2"></i>{{ error }}
    </div>
  </div>
</template>

<script>
  import { Editor, EditorContent } from '@tiptap/vue-3'
  import StarterKit from '@tiptap/starter-kit'
  import TaskList from '@tiptap/extension-task-list'
  import TaskItem from '@tiptap/extension-task-item'
  import Link from '@tiptap/extension-link'

  export default {
    name: 'TipTap',
    components: { EditorContent },
    props: [
      'value',
      'disabled',
      'error',
      'noButtons',
      'autofocus',
      'maxRows',
      'minRows',
      'label'
    ],
    data: () => ({
      editor: null,
      throttleInputEvent: null,
      focused: false,
      minHeight: 0
    }),
    watch: {
      value(val) {
        if (this.editor.getHTML() !== val) this.editor.commands.setContent(val)
      },
      disabled(val) {
        this.editor.setEditable(!val)
      }
    },
    mounted() {
      const _this = this
      this.editor = new Editor({
        autofocus: this.autofocus,
        content: this.value,
        extensions: [StarterKit, TaskList, TaskItem, Link],
        onUpdate: () => {
          clearTimeout(this.throttleInputEvent)
          _this.throttleInputEvent = setTimeout(() => _this.onUpdate(), 300)
        },
        onFocus() {
          _this.focused = true
        },
        onBlur() {
          _this.onUpdate()
          _this.focused = false
        }
      })
      this.editor.setEditable(!this.disabled)
    },
    beforeUnmount() {
      this.editor.destroy()
    },
    computed: {
      hasValue() {
        return !!this.value && this.value !== '<p></p>'
      }
    },
    methods: {
      addonClick(command, arg) {
        if (this.disabled) return
        this.editor.commands[command](arg)
        this.editor.commands.focus()
      },
      onUpdate() {
        this.$emit('input', this.editor.getHTML())
      },
      focus() {
        this.editor.commands.focus()
      }
    }
  }
</script>

<style>
  /* Wrapper */
  .TipTapWrap {
    position: relative;
    border: 1px solid var(--app-border);
    border-radius: var(--app-radius-md);
    background: var(--app-bg-input);
    transition: border-color var(--app-transition), background var(--app-transition);
  }

  .TipTapWrap:focus-within {
    border-color: rgba(61, 106, 144, 0.5);
    background: rgba(0, 0, 0, 0.02);
  }

  .TipTapWrap.disabled {
    background: rgba(0, 0, 0, 0.04);
    border-color: rgba(0, 0, 0, 0.06);
    pointer-events: none;
    opacity: 0.55;
  }

  /* Toolbar */
  .TipTapWrap .tiptap-toolbar {
    display: flex;
    align-items: center;
    gap: 1px;
    padding: 6px 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  }

  .TipTapWrap .tiptap-toolbar i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 5px;
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.4);
    cursor: pointer;
    transition: color 0.12s, background 0.12s;
    padding: 0;
  }

  .TipTapWrap .tiptap-toolbar i:hover {
    color: rgba(0, 0, 0, 0.75);
    background: rgba(0, 0, 0, 0.07);
  }

  .TipTapWrap .tiptap-toolbar i.is-active {
    color: var(--primary);
    background: var(--primary-muted);
  }

  /* Editor content */
  .editorContent {
    overflow: auto;
  }

  .editorContent .ProseMirror {
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.6;
    padding: 8px 10px;
    border: none;
    outline: none;
    color: rgba(0, 0, 0, 0.8);
    background: transparent;
  }

  .editorContent .ProseMirror p {
    margin: 0 0 0.4em;
  }

  .editorContent .ProseMirror p:last-child {
    margin-bottom: 0;
  }

  /* Prose elements */
  .editorContent .ProseMirror blockquote {
    border-left: 3px solid rgba(61, 106, 144, 0.35);
    margin: 0.5em 0;
    padding: 0.2em 0 0.2em 0.75em;
    color: rgba(0, 0, 0, 0.55);
    font-style: italic;
  }

  .editorContent .ProseMirror code {
    background: rgba(0, 0, 0, 0.06);
    border-radius: 4px;
    padding: 1px 5px;
    font-size: 0.82rem;
    font-family: monospace;
    color: rgba(0, 0, 0, 0.7);
  }

  .editorContent .ProseMirror pre {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 6px;
    padding: 0.6em 0.8em;
    margin: 0.4em 0;
    overflow-x: auto;
  }

  .editorContent .ProseMirror pre code {
    background: none;
    padding: 0;
  }

  .editorContent .ProseMirror ul,
  .editorContent .ProseMirror ol {
    padding-left: 1.4em;
    margin: 0.3em 0;
  }

  .editorContent .ProseMirror li {
    margin: 0.15em 0;
  }

  .editorContent .ProseMirror h3 {
    font-size: 1rem;
    font-weight: 700;
    margin: 0.6em 0 0.2em;
    color: rgba(0, 0, 0, 0.8);
  }

  .editorContent .ProseMirror hr {
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    margin: 0.6em 0;
  }

  /* Task list */
  ul[data-type='taskList'] {
    padding-left: 4px;
    list-style: none;
  }

  ul[data-type='taskList'] li {
    display: flex;
    align-items: flex-start;
    gap: 6px;
  }

  ul[data-type='taskList'] li > label {
    flex-shrink: 0;
    margin-top: 2px;
  }

  ul[data-type='taskList'] li input[type='checkbox'] {
    width: 14px;
    height: 14px;
    accent-color: var(--primary);
    cursor: pointer;
  }

  /* Floating label */
  .editorContent ~ label {
    position: absolute;
    left: 1px;
    top: -18px;
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.55);
    transition: 0.2s ease;
    pointer-events: none;
    letter-spacing: 0.01em;
  }

  .TipTapWrap:focus-within .editorContent ~ label {
    top: -18px;
    left: 1px;
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.55);
  }

  /* Error */
  .error-text {
    font-size: 0.78rem;
    color: #b03a3a;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
  }
</style>
